import React from "react";
import { Link } from "react-router-dom";
import { Truck, ShieldCheck, Sparkles, Globe, Heart, Award } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useSEO, siteUrl, videoObjectJsonLd, breadcrumbJsonLd } from "@/lib/seo";
import CTASection from "@/components/CTASection";
import VideoFeature from "@/components/VideoFeature";

const ABOUT_VIDEO = {
  id: "Z0amOxxHmDM",
  uploadDate: "2017-04-15",
  thumbnail: "https://i.ytimg.com/vi/Z0amOxxHmDM/maxresdefault.jpg",
  watchUrl: "https://www.youtube.com/watch?v=Z0amOxxHmDM",
  embedUrl: "https://www.youtube-nocookie.com/embed/Z0amOxxHmDM",
};

const ValueCard = ({ icon: Icon, title, body }) => (
  <div className="rounded-2xl border border-white/10 bg-[#0F0F23] p-7 card-hover">
    <div className="w-11 h-11 rounded-lg bg-[#00D4FF]/10 border border-[#00D4FF]/20 flex items-center justify-center mb-5">
      <Icon className="w-5 h-5 text-[#00D4FF]" strokeWidth={1.8} />
    </div>
    <h3 className="text-white text-lg font-semibold mb-2">{title}</h3>
    <p className="text-white/55 text-sm leading-relaxed">{body}</p>
  </div>
);

const About = () => {
  const { lang } = useLang();

  useSEO({
    title: lang === "nl"
      ? "Over Wasz.nl — Mobiele truckwash met digitaal hart"
      : "About Wasz.nl — Mobile truckwash with a digital heart",
    description: lang === "nl"
      ? "Wasz.nl combineert vakkundige mobiele truckwash met een eigen digitaal klantportaal: WaszBase. Vanuit Almere bedienen wij heel transport-Nederland."
      : "Wasz.nl combines skilled mobile truckwash with our own digital customer portal: WaszBase. From Almere we serve all of transport Netherlands.",
    canonical: siteUrl("/over-ons"),
    lang,
    jsonLd: [
      {
        id: "breadcrumb-jsonld",
        data: breadcrumbJsonLd([
          { name: lang === "nl" ? "Home" : "Home", url: siteUrl("/") },
          { name: lang === "nl" ? "Over Ons" : "About Us", url: siteUrl("/over-ons") },
        ]),
      },
      {
        id: "about-video-jsonld",
        data: videoObjectJsonLd({
          name: lang === "nl"
            ? "Wasz.nl in Doe Maar Duurzaam — duurzame mobiele truckwash"
            : "Wasz.nl on Doe Maar Duurzaam — sustainable mobile truckwash",
          description: lang === "nl"
            ? "Reportage uit het programma Doe Maar Duurzaam — uitgezonden door AVROTROS en RTL-Z — over Wasz.nl: hoe mobiele truckwash bijdraagt aan duurzamer transport en efficiënter wagenparkbeheer."
            : "Reportage from the programme Doe Maar Duurzaam — broadcast by AVROTROS and RTL-Z — on Wasz.nl: how mobile truckwash contributes to more sustainable transport and more efficient fleet management.",
          thumbnailUrl: ABOUT_VIDEO.thumbnail,
          uploadDate: ABOUT_VIDEO.uploadDate,
          contentUrl: ABOUT_VIDEO.watchUrl,
          embedUrl: ABOUT_VIDEO.embedUrl,
        }),
      },
    ],
  });

  const values_nl = [
    { icon: Truck, title: "Operationele scherpte", body: "Geen vertraging, geen halve maatregelen. Onze teams werken met militaire planning en transport-DNA." },
    { icon: ShieldCheck, title: "Compliance ingebouwd", body: "Elke wasbeurt produceert automatisch het bewijs dat HACCP- en NVWA-controles vragen. Geen losse paperwork." },
    { icon: Sparkles, title: "Premium uitvoering", body: "Vakvolwassen operators, professionele apparatuur en biologisch afbreekbare middelen. Geen compromis." },
    { icon: Globe, title: "Landelijke dekking", body: "Vanuit Almere en Den Haag bedienen wij heel Nederland. Eén partner, één contract, één dossier." },
    { icon: Heart, title: "Lange relaties", body: "We houden klanten lang. Onze gemiddelde klantrelatie loopt over meerdere jaren — daar zijn we trots op." },
    { icon: Award, title: "Digitaal als standaard", body: "WaszBase is geen extra; het is de standaard. Elke klant krijgt een digitaal dossier per kenteken." },
  ];

  const values_en = [
    { icon: Truck, title: "Operational sharpness", body: "No delays, no half measures. Our teams work with military planning and transport DNA." },
    { icon: ShieldCheck, title: "Compliance built-in", body: "Every wash automatically produces the evidence HACCP and NVWA inspections demand. No loose paperwork." },
    { icon: Sparkles, title: "Premium execution", body: "Mature operators, professional equipment and biodegradable agents. No compromise." },
    { icon: Globe, title: "Nationwide coverage", body: "From Almere and The Hague we serve all of the Netherlands. One partner, one contract, one dossier." },
    { icon: Heart, title: "Long relationships", body: "We keep customers long. Our average customer relationship runs multiple years — we're proud of that." },
    { icon: Award, title: "Digital as standard", body: "WaszBase is not an extra; it's the standard. Every customer gets a digital dossier per license plate." },
  ];

  const values = lang === "nl" ? values_nl : values_en;

  return (
    <main data-testid="about-page" className="min-h-screen">
      <section className="pt-20 pb-16 sm:pt-28 sm:pb-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="section-overline mb-3">{lang === "nl" ? "Over Wasz.nl" : "About Wasz.nl"}</p>
            <h1 className="text-4xl sm:text-6xl font-black tracking-tighter text-white leading-[1.05]">
              {lang === "nl" ? (
                <>Operationele <span className="text-[#00D4FF]">precisie</span>, met een digitaal hart.</>
              ) : (
                <>Operational <span className="text-[#00D4FF]">precision</span>, with a digital heart.</>
              )}
            </h1>
            <p className="mt-6 text-white/65 text-lg leading-relaxed">
              {lang === "nl"
                ? "Wasz.nl is in 2016 opgezet door professionals uit transport en logistiek met meer dan 25 jaar ervaring in vrachtwagenreiniging. We zagen dat reiniging vaak de laatste schakel was in een verder digitaal-volwassen vloot. Wij bouwden het tegenovergestelde: reiniging met een eigen software-laag eronder."
                : "Wasz.nl was founded in 2016 by transport and logistics professionals with more than 25 years of experience in truck cleaning. We saw that cleaning was often the last link in an otherwise digitally mature fleet. We built the opposite: cleaning with its own software layer underneath."}
            </p>
            <p className="mt-5 text-white/65 text-lg leading-relaxed">
              {lang === "nl"
                ? "Wij zijn een volledig mobiel bedrijf — wij hebben geen vaste wasstraat waar u uw vloot heen rijdt. Onze service-units komen naar uw locatie, in heel Nederland, altijd op afspraak en met een vast tijdsvenster per klant."
                : "We are a fully mobile company — we don't operate a fixed wash bay you drive your fleet to. Our service units come to your site, anywhere in the Netherlands, always by appointment and with a fixed time slot per customer."}
            </p>
            <Link to="/contact" className="mt-9 inline-block">
              <button className="btn-primary" data-testid="about-cta">
                {lang === "nl" ? "Plan een kennismaking" : "Schedule an intro"}
              </button>
            </Link>
            <Link
              to="/het-verhaal-achter-wasz"
              data-testid="about-story-link"
              className="mt-4 ml-0 sm:ml-3 inline-flex items-center gap-1.5 text-sm font-semibold text-white/70 hover:text-[#00D4FF] transition-colors"
            >
              {lang === "nl" ? "Lees het volledige verhaal" : "Read the full story"} →
            </Link>

            {/* Contextual internal links (SEO authority distribution) */}
            <div
              data-testid="about-internal-links"
              className="mt-8 pt-6 border-t border-white/5 flex flex-wrap gap-x-5 gap-y-2 text-xs text-white/50"
            >
              <span className="uppercase tracking-[0.16em] text-white/35 font-semibold">
                {lang === "nl" ? "Verken" : "Explore"}:
              </span>
              <Link to="/mobiele-truckwash" className="hover:text-[#00D4FF] underline-offset-4 hover:underline transition-colors">
                {lang === "nl" ? "Mobiele truckwash" : "Mobile truckwash"}
              </Link>
              <Link to="/vrachtwagenreiniging" className="hover:text-[#00D4FF] underline-offset-4 hover:underline transition-colors">
                {lang === "nl" ? "Vrachtwagenreiniging" : "Truck cleaning"}
              </Link>
              <Link to="/wagenpark-reiniging" className="hover:text-[#00D4FF] underline-offset-4 hover:underline transition-colors">
                {lang === "nl" ? "Wagenparkreiniging" : "Fleet cleaning"}
              </Link>
              <Link to="/waszbase" className="hover:text-[#00D4FF] underline-offset-4 hover:underline transition-colors">
                WaszBase
              </Link>
              <Link to="/haccp-certificaten" className="hover:text-[#00D4FF] underline-offset-4 hover:underline transition-colors">
                {lang === "nl" ? "HACCP-certificaten" : "HACCP certificates"}
              </Link>
            </div>
          </div>
          <div className="relative">
            <div className="rounded-2xl overflow-hidden border border-white/10 aspect-[4/3]">
              <img
                src="/images/project17.jpg"
                alt="Wasz.nl mobile truckwash team in operation"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-[#0F0F23] border border-white/10 rounded-xl p-5 hidden sm:flex gap-5 items-center">
              <div>
                <div className="text-xs uppercase tracking-wider text-white/40 mb-1">{lang === "nl" ? "Sinds" : "Since"}</div>
                <div className="text-3xl font-extrabold text-white">2016</div>
              </div>
              <div className="w-px h-10 bg-white/10" />
              <div>
                <div className="text-xs uppercase tracking-wider text-white/40 mb-1">{lang === "nl" ? "Ervaring" : "Experience"}</div>
                <div className="text-3xl font-extrabold text-white">25+ <span className="text-base font-semibold text-white/60">{lang === "nl" ? "jaar" : "yrs"}</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#0a0a18] border-y border-white/5">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <p className="section-overline mb-3">{lang === "nl" ? "Onze waarden" : "Our values"}</p>
            <h2 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight">
              {lang === "nl" ? "Zes principes. Geen uitzonderingen." : "Six principles. No exceptions."}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {values.map((v, i) => (
              <ValueCard key={i} {...v} />
            ))}
          </div>
        </div>
      </section>

      {/* DOE MAAR DUURZAAM TV FEATURE */}
      <section
        data-testid="about-video-section"
        id="doe-maar-duurzaam"
        className="py-24 sm:py-28"
      >
        <div className="mx-auto max-w-5xl px-6 lg:px-8">
          <div className="max-w-2xl mb-10">
            <p className="section-overline mb-3">
              {lang === "nl" ? "Op televisie · AVROTROS & RTL-Z" : "On television · AVROTROS & RTL-Z"}
            </p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight leading-[1.1] mb-4">
              {lang === "nl" ? (
                <>Wasz.nl bij <span className="text-[#00D4FF]">Doe Maar Duurzaam</span>.</>
              ) : (
                <>Wasz.nl on <span className="text-[#00D4FF]">Doe Maar Duurzaam</span>.</>
              )}
            </h2>
            <p className="text-white/65 text-base leading-relaxed">
              {lang === "nl"
                ? "Een korte reportage uit het programma Doe Maar Duurzaam — uitgezonden door AVROTROS en RTL-Z — over hoe wij mobiele truckwash inzetten om transportbedrijven duurzamer te laten werken. Onderdeel van ons verhaal sinds de oprichting in 2016."
                : "A short reportage from the programme Doe Maar Duurzaam — broadcast by AVROTROS and RTL-Z — on how we use mobile truckwash to help transport operators work more sustainably. Part of our story since founding in 2016."}
            </p>
          </div>
          <VideoFeature
            videoId={ABOUT_VIDEO.id}
            badge={lang === "nl" ? "Uitzending" : "Broadcast"}
            title={lang === "nl"
              ? "Wasz.nl bij Doe Maar Duurzaam — mobiele truckwash & duurzaam wagenparkbeheer"
              : "Wasz.nl on Doe Maar Duurzaam — mobile truckwash & sustainable fleet management"}
            description={lang === "nl"
              ? "Reportage over duurzaam transport en mobiele vrachtwagenreiniging op locatie."
              : "A reportage on sustainable transport and mobile on-site truck cleaning."}
            watchUrl={ABOUT_VIDEO.watchUrl}
            testIdPrefix="about-video"
            ariaLabel={lang === "nl"
              ? "Speel video af: Wasz.nl in Doe Maar Duurzaam"
              : "Play video: Wasz.nl on Doe Maar Duurzaam"}
          />
        </div>
      </section>

      <CTASection />
    </main>
  );
};

export default About;
